import React, { useState, useRef, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FeedbackToolWrapper } from '../Shared/FeedbackTool';
import ImprovedSecondarySideBar from '../ImprovedSecondarySideBar';
import MenuButton from '../MenuButton';
import { isTabletView } from '../ReactiveRender';
import ShortAnswerCard from './ShortAnswerCard';
import {
  PopUpCard,
  PopUpCardImg,
  PopUpCardText,
} from '../Shared/FeedbackTool/style';
import {
  PopupBackground,
  PopupContainer,
  PopupTitleContainer,
  PopupTitle,
  PopupTitleImg,
  PopupDialogContentBox,
  PopupDialogContentBoxLeft,
  PopupDialogContentBoxRight,
  CardContainer,
  Card,
  CardImgCont,
  CardImg,
  CardTitle,
  ImportFile,
  CardImgDoc,
  PreviewContainer,
  PreviewIconCont,
  PrevieImg,
  Previewpara,
  CreateButton,
  ButtonText,
} from '../Shared/Dialogs/popupStyle';
import {
  PreviewDialogContainer,
  PreviewDialogHeader,
  PreviewDialogTitle,
  PreviewDialogHeaderActions,
  PreviewEditButton,
  PreviewDialogContent,
  CriteriaHeaderRow,
  MarksHeaderLabel,
  CriteriaHeaderLabel,
  CriteriaListContainer,
  CriteriaRow,
  MarksBox,
  CriteriaBox,
  PreviewContentWrapper,
  SmallMarksBox,
  SmallCriteriaBox,
  ImportButtonContainer,
  ImportButtonLabel,
  ImportButtonIcon,
  ImportButtonText,
  TemplateCard,
  TemplateTitleBorder,
} from './style';
import {
  importJsonFile,
  exportJsonFile,
} from '../../components2/markingCriteria';
import { isNullOrEmpty } from '../../utils/arrays';
import {
  createShortAnswer,
  deleteShortAnswer,
  getShortAnswer,
  updateShortAnswerPreferences,
} from '../../service';
import { buildClonePayload } from '../../utils/subjects';
import { useApiSubjects } from '../../hooks/useSubjects';
import {
  useShortAnswers,
  useShortAnswersWithPreferences,
} from '../state/hooks';
import Loader from '../Loader';
import PlusIcon from '../../static/img/Plus-blue.svg';
import DownloadIcon from '../../static/img/Download.svg';
import closecircle from '../../static/img/closecircle.svg';
import editIcon from '../../static/icons/edit_icon_purple_24.svg';
import Doc from '../../static/img/doc.svg';
import PreviewIcon from '../../static/img/preview.svg';
import UploadIcon from '../../static/icons/share-download-icon.svg';

function ShortAnswers() {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [selectedShortAnswers, setSelectedShortAnswers] = useState(new Set());
  const [showHidden, setShowHidden] = useState(false);
  const [isShowSystemShortAnswersOnly, setIsShowSystemShortAnswersOnly] =
    useState(false);
  const [importedShortAnswer, setImportedShortAnswer] = useState({});
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState();
  const [shortAnswerPreviewDialog, setShortAnswerPreviewDialog] =
    useState(false);
  const [previewShortAnswer, setPreviewShortAnswer] = useState(null);
  const selectedRef = useRef(null);
  const tabletView = isTabletView();

  const {
    data: shortAnswersData,
    isLoadingdata: isLoadingShortAnswers,
    setData: setShortAnswers,
    resetData: resetShortAnswers,
  } = useShortAnswers();

  const {
    data: shortAnswersWithPreferences,
    isLoadingdata: isLoadingShortAnswersWithPreferences,
    setData: setShortAnswersWithPreferences,
    resetData: resetShortAnswersWithPreferences,
  } = useShortAnswersWithPreferences();
  const { apiSubjects } = useApiSubjects();

  const originalShortAnswers = Array.isArray(shortAnswersData)
    ? shortAnswersData
    : [];
  const systemShortAnswers = originalShortAnswers.filter(
    (answer) => answer.isSystem
  );

  const hasActiveFilters = useMemo(() => {
    return isShowSystemShortAnswersOnly || showHidden;
  }, [isShowSystemShortAnswersOnly, showHidden]);

  const isDragDisabled =
    hasActiveFilters || isLoadingShortAnswersWithPreferences;

  const handleSelectShortAnswer = (shortAnswerId) => {
    const newSelected = new Set(selectedShortAnswers);
    if (newSelected.has(shortAnswerId)) {
      newSelected.delete(shortAnswerId);
    } else {
      newSelected.add(shortAnswerId);
    }
    setSelectedShortAnswers(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedShortAnswers.size === (filteredItems?.length || 0)) {
      setSelectedShortAnswers(new Set());
    } else {
      const allIds = filteredItems?.map((sa) => sa.id) || [];
      setSelectedShortAnswers(new Set(allIds));
    }
  };

  const handleShortAnswerImport = async (event) => {
    try {
      const importedJsonFile = await importJsonFile(event, ['sa']);
      setImportedShortAnswer(importedJsonFile);
      setShowImportDialog(true);
    } catch (err) {
      console.error('Error importing short answer:', err);
    }
  };

  const createNewShortAnswer = (shortAnswer = undefined) => {
    const newAnswer = {
      title: 'Untitled Short Answer',
      highestMarks: 10,
      criterias: [
        {
          marks: 5,
          criteria: 'Partial credit',
        },
        {
          marks: 10,
          criteria: 'Full credit',
        },
      ],
    };

    createShortAnswer(shortAnswer || newAnswer)
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: ['feedbackTool', 'items', 'shortAnswers'],
        });
        queryClient.invalidateQueries({
          queryKey: ['feedbackTool', 'itemsCount', 'shortAnswers'],
        });
        resetShortAnswers();
        resetShortAnswersWithPreferences();

        if (response?.id) {
          history.push(`/tools/shortanswers/${response.id}`);
        }
      })
      .catch((error) => {
        console.error('Error creating new short answer:', error);
      });
  };

  const createSystemShortAnswer = (shortAnswer) => {
    if (!shortAnswer) {
      return;
    }

    const { highestMarks, criterias } = shortAnswer;
    // Strip criterion ids (and other backend-stamped fields) so the cloned
    // template gets fresh ones from the backend; reusing source ids would
    // collide with the template's own criteria on subsequent edits. Fall
    // back to `description` for legacy short answers where the criterion
    // text lives on the old field name. Keep marksFrom so ranged templates
    // clone correctly.
    const cleanedCriterias =
      criterias?.map((criteriaItem) => ({
        marks: criteriaItem.marks,
        criteria: criteriaItem.criteria || criteriaItem.description || '',
        ...(criteriaItem.marksFrom != null && {
          marksFrom: criteriaItem.marksFrom,
        }),
      })) || [];
    const newObject = buildClonePayload(shortAnswer, apiSubjects, {
      highestMarks,
      criterias: cleanedCriterias,
    });

    createShortAnswer(newObject)
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: ['feedbackTool', 'items', 'shortAnswers'],
        });
        queryClient.invalidateQueries({
          queryKey: ['feedbackTool', 'itemsCount', 'shortAnswers'],
        });

        resetShortAnswers();
        resetShortAnswersWithPreferences();
      })
      .catch((error) => {
        console.error('Error creating new short answer:', error);
      });
  };

  const cloneShortAnswer = async (shortAnswer) => {
    const { highestMarks, criterias } = shortAnswer;
    const cleanedCriterias =
      criterias?.map((criteriaItem) => ({
        marks: criteriaItem.marks,
        criteria: criteriaItem.criteria,
        ...(criteriaItem.marksFrom != null && {
          marksFrom: criteriaItem.marksFrom,
        }),
      })) || [];
    const source = shortAnswer.id
      ? await getShortAnswer(shortAnswer.id)
          .then((data) =>
            Array.isArray(data)
              ? data.find((i) => i.id === shortAnswer.id) || data[0]
              : data
          )
          .catch(() => shortAnswer)
      : shortAnswer;
    const newObject = buildClonePayload(source, apiSubjects, {
      highestMarks,
      criterias: cleanedCriterias,
    });
    console.log('[clone short answer] source (for metadata):', source);
    console.log('[clone short answer] API payload:', newObject);

    createShortAnswer(newObject)
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: ['feedbackTool', 'items', 'shortAnswers'],
        });
        queryClient.invalidateQueries({
          queryKey: ['feedbackTool', 'itemsCount', 'shortAnswers'],
        });
        resetShortAnswers();
        resetShortAnswersWithPreferences();
      })
      .catch((error) => {
        console.error('Error cloning short answer:', error);
      });
  };

  const downloadShortAnswer = (shortAnswer) => {
    const extractedShortAnswer = {
      title: shortAnswer.title,
      highestMarks: shortAnswer.highestMarks,
      criterias: shortAnswer.criterias,
    };
    exportJsonFile(extractedShortAnswer, extractedShortAnswer.title, 'sa');
  };

  const handleDownload = (item) => {
    if (item) {
      const extractedShortAnswer = {
        title: item.title,
        highestMarks: item.highestMarks,
        criterias: item.criterias,
      };
      exportJsonFile(extractedShortAnswer, extractedShortAnswer.title, 'sa');
    }
  };

  const handleClone = async (shortAnswer) => {
    if (shortAnswer) {
      const { highestMarks, criterias } = shortAnswer;
      const cleanedCriterias =
        criterias?.map((criteriaItem) => ({
          marks: criteriaItem.marks,
          criteria: criteriaItem.criteria,
          ...(criteriaItem.marksFrom != null && {
            marksFrom: criteriaItem.marksFrom,
          }),
        })) || [];
      const source = shortAnswer.id
        ? await getShortAnswer(shortAnswer.id)
            .then((data) =>
              Array.isArray(data)
                ? data.find((i) => i.id === shortAnswer.id) || data[0]
                : data
            )
            .catch(() => shortAnswer)
        : shortAnswer;
      const newObject = buildClonePayload(source, apiSubjects, {
        highestMarks,
        criterias: cleanedCriterias,
      });
      console.log('[clone short answer] source (for metadata):', source);
      console.log('[clone short answer] API payload:', newObject);

      createShortAnswer(newObject)
        .then(async (res) => {
          const selfLink = res?._links?.self?.href || res?.id;
          let newShortAnswerId = null;
          if (selfLink) {
            if (typeof selfLink === 'string' && selfLink.includes('/')) {
              const urlParts = selfLink.split('/');
              newShortAnswerId = urlParts[urlParts.length - 1];
            } else {
              newShortAnswerId = selfLink;
            }
          }
          if (newShortAnswerId) {
            const originalSortOrder = shortAnswer.sortOrder ?? 999;
            const newSortOrder = originalSortOrder + 1;
            try {
              await updateShortAnswerPreferences('REORDER', {
                toolIds: [newShortAnswerId],
                newSortOrder,
              });
            } catch (error) {
              console.error(
                'Error setting sort order for duplicated short answer:',
                error
              );
            }
          }
          queryClient.invalidateQueries({
            queryKey: ['feedbackTool', 'items', 'shortAnswers'],
          });
          queryClient.invalidateQueries({
            queryKey: ['feedbackTool', 'itemsCount', 'shortAnswers'],
          });
          resetShortAnswers();
          resetShortAnswersWithPreferences();
        })
        .catch((error) => {
          console.error('Cloning error:', error);
        });
    }
  };

  const handlePreview = (shortAnswer) => {
    if (shortAnswer && !isNullOrEmpty(shortAnswer?.criterias)) {
      setShortAnswerPreviewDialog(true);
      setPreviewShortAnswer(shortAnswer);
    }
  };

  const handleOpenClick = (shortAnswer) => {
    if (shortAnswer && !shortAnswer.isSystem) {
      history.push(`/tools/shortanswers/${shortAnswer.id}`);
    }
  };

  const deleteShortAnswerHandler = (shortAnswerId) => {
    deleteShortAnswer(shortAnswerId)
      .then(() => {
        resetShortAnswers();
        resetShortAnswersWithPreferences();
      })
      .catch((error) => {
        console.error('Error deleting short answer:', error);
      });
  };

  const renderCreateOptions = () => (
    <>
      <PopUpCard onClick={() => history.push('/tools/shortanswers/new')}>
        <PopUpCardImg src={PlusIcon} />
        <PopUpCardText>Create Your Own</PopUpCardText>
      </PopUpCard>
      <PopUpCard onClick={() => history.push('/tools/shortanswers/new')}>
        <PopUpCardImg src={PlusIcon} />
        <PopUpCardText>Add Another Short Answer</PopUpCardText>
      </PopUpCard>
      <PopUpCard onClick={() => setShowImportDialog(true)}>
        <PopUpCardImg src={DownloadIcon} />
        <PopUpCardText>Import</PopUpCardText>
      </PopUpCard>
    </>
  );

  const renderImportDialog = () => {
    if (
      showImportDialog &&
      importedShortAnswer &&
      Object.keys(importedShortAnswer).length > 0
    ) {
      const sortedCriterias =
        importedShortAnswer.criterias
          ?.slice()
          .sort((a, b) => b.marks - a.marks) || [];
      return (
        <PopupBackground>
          <PreviewDialogContainer>
            <PreviewDialogHeader>
              <PreviewDialogTitle>
                {importedShortAnswer.title}
              </PreviewDialogTitle>
              <PopupTitleImg
                onClick={() => setShowImportDialog(false)}
                src={closecircle}
              />
            </PreviewDialogHeader>
            <PreviewDialogContent>
              <CriteriaHeaderRow>
                <MarksHeaderLabel>Marks</MarksHeaderLabel>
                <CriteriaHeaderLabel>Criteria</CriteriaHeaderLabel>
              </CriteriaHeaderRow>
              <CriteriaListContainer>
                {sortedCriterias.map((criteria, idx) => (
                  <CriteriaRow key={idx}>
                    <MarksBox>
                      {criteria.marksFrom != null
                        ? `${criteria.marksFrom}-${criteria.marks}`
                        : criteria.marks}
                    </MarksBox>
                    <CriteriaBox>{criteria.criteria}</CriteriaBox>
                  </CriteriaRow>
                ))}
              </CriteriaListContainer>
            </PreviewDialogContent>
            <ImportButtonContainer>
              <CreateButton
                onClick={() => {
                  createNewShortAnswer(importedShortAnswer);
                  setShowImportDialog(false);
                }}
              >
                <ButtonText>Import</ButtonText>
              </CreateButton>
            </ImportButtonContainer>
          </PreviewDialogContainer>
        </PopupBackground>
      );
    }
    return null;
  };

  const renderCreatePopup = ({ onClose }) => {
    const selectAnswerFun = (e, answer) => {
      e.preventDefault();
      setSelectedAnswer(answer);
      setTimeout(() => {
        if (selectedRef.current) {
          selectedRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }
      }, 0);
    };

    const handleImportFromPopup = async (event) => {
      try {
        const importedJsonFile = await importJsonFile(event, ['sa']);
        setImportedShortAnswer(importedJsonFile);
        setShowImportDialog(true);
        onClose();
      } catch (err) {
        console.error('Error importing short answer:', err);
      }
    };

    return (
      <PopupBackground>
        <PopupContainer>
          <PopupTitleContainer>
            <PopupTitle>New Short Answer</PopupTitle>
            <PopupTitleImg onClick={onClose} src={closecircle} />
          </PopupTitleContainer>
          <PopupDialogContentBox>
            <PopupDialogContentBoxLeft>
              <CardContainer>
                <Card onClick={() => history.push('/tools/shortanswers/new')}>
                  <CardImgCont>
                    <CardImg src={PlusIcon} />
                  </CardImgCont>
                  <CardTitle>Create Your Own</CardTitle>
                </Card>
              </CardContainer>
              {systemShortAnswers && systemShortAnswers.length > 0 && (
                <TemplateTitleBorder>Use a template</TemplateTitleBorder>
              )}

              {systemShortAnswers?.map((answer) => {
                return (
                  <TemplateCard
                    key={answer.id}
                    onClick={(e) => selectAnswerFun(e, answer)}
                    ref={answer.id === selectedAnswer?.id ? selectedRef : null}
                    isSelected={answer.id === selectedAnswer?.id}
                  >
                    <CardImgDoc src={Doc} />
                    <CardTitle>{answer.title}</CardTitle>
                  </TemplateCard>
                );
              })}
            </PopupDialogContentBoxLeft>
            <PopupDialogContentBoxRight>
              <PreviewContainer>
                <PreviewIconCont>
                  <PrevieImg src={PreviewIcon} />
                  <Previewpara>Preview Template</Previewpara>
                </PreviewIconCont>
                {systemShortAnswers &&
                  systemShortAnswers.length > 0 &&
                  selectedAnswer && (
                    <CreateButton
                      onClick={() => {
                        createSystemShortAnswer(selectedAnswer);
                        onClose();
                      }}
                    >
                      <ButtonText>Add selected template</ButtonText>
                    </CreateButton>
                  )}
              </PreviewContainer>

              {selectedAnswer && (
                <PreviewContentWrapper>
                  <CriteriaHeaderRow>
                    <MarksHeaderLabel>Marks</MarksHeaderLabel>
                    <CriteriaHeaderLabel>Criteria</CriteriaHeaderLabel>
                  </CriteriaHeaderRow>
                  <CriteriaListContainer>
                    {selectedAnswer.criterias
                      ?.slice()
                      .sort((a, b) => b.marks - a.marks)
                      .map((criteria, idx) => (
                        <CriteriaRow key={idx}>
                          <SmallMarksBox>
                            {criteria.marksFrom != null
                              ? `${criteria.marksFrom}-${criteria.marks}`
                              : criteria.marks}
                          </SmallMarksBox>
                          <SmallCriteriaBox>
                            {criteria.criteria}
                          </SmallCriteriaBox>
                        </CriteriaRow>
                      ))}
                  </CriteriaListContainer>
                </PreviewContentWrapper>
              )}

              <ImportButtonContainer>
                <ImportButtonLabel>
                  <ImportButtonIcon src={UploadIcon} />
                  <ImportButtonText>Import</ImportButtonText>
                  <ImportFile
                    type="file"
                    accept=".sa"
                    onChange={handleImportFromPopup}
                  />
                </ImportButtonLabel>
              </ImportButtonContainer>
            </PopupDialogContentBoxRight>
          </PopupDialogContentBox>
        </PopupContainer>
      </PopupBackground>
    );
  };

  const renderItem = (shortAnswer) => (
    <ShortAnswerCard
      shortAnswer={shortAnswer}
      isSelected={selectedShortAnswers.has(shortAnswer.id)}
      onSelect={() => handleSelectShortAnswer(shortAnswer.id)}
      showCheckbox={true}
      showActions={true}
      isSystem={shortAnswer.isSystem}
      cloneShortAnswer={cloneShortAnswer}
      handleDownload={handleDownload}
      handleClone={handleClone}
      handlePreview={handlePreview}
      handleOpenClick={handleOpenClick}
    />
  );

  const mergedShortAnswers = originalShortAnswers.map((answer) => {
    const preference = shortAnswersWithPreferences?.find(
      (pref) => pref.id === answer.id
    );
    const merged = preference
      ? {
          ...answer,
          isVisible: preference.isVisible,
          visible:
            preference.visible !== false && preference.isVisible !== false,
          sortOrder: preference.sortOrder,
        }
      : answer;
    return merged;
  });

  const filteredItems = mergedShortAnswers.filter((shortAnswer) => {
    if (isShowSystemShortAnswersOnly) {
      if (shortAnswer.isSystem) {
        return false;
      }
    }

    if (showHidden) {
      return !shortAnswer.visible;
    } else {
      return shortAnswer.visible;
    }
  });

  const resetShortAnswersData = () => {
    resetShortAnswers();
    resetShortAnswersWithPreferences();
  };

  // Removed full-page loader to prevent the flash that the user saw on
  // /tools/rubrics — see AccountSettingRoot for the full rationale. Same
  // pattern: Loader is position:fixed and covered the page chrome while
  // data loaded, so the user perceived an unwanted second full-page load.
  // The page chrome handles empty state safely.
  // if (isLoadingShortAnswers || isLoadingShortAnswersWithPreferences) return <Loader />;

  return (
    <>
      <FeedbackToolWrapper
        enableReactQuery={true}
        context="shortAnswers"
        selectedItems={selectedShortAnswers}
        setSelectedCriteria={setSelectedShortAnswers}
        onImport={handleShortAnswerImport}
        onSelectAll={handleSelectAll}
        renderItem={renderItem}
        renderCreateOptions={renderCreateOptions}
        renderImportDialog={renderImportDialog}
        isDragDisabled={isDragDisabled}
        hasActiveFilters={hasActiveFilters}
        tabletView={tabletView}
        MenuButton={MenuButton}
        SecondarySidebar={ImprovedSecondarySideBar}
        showHidden={showHidden}
        setShowHidden={setShowHidden}
        showOnlyMineFilter={isShowSystemShortAnswersOnly}
        setShowOnlyMineFilter={setIsShowSystemShortAnswersOnly}
        allMarkingCriteria={mergedShortAnswers}
        resetMarkingCriterias={resetShortAnswersData}
        usePopupForCreate={true}
        renderCreatePopup={renderCreatePopup}
      />
      {shortAnswerPreviewDialog && previewShortAnswer && (
        <PopupBackground>
          <PreviewDialogContainer>
            <PreviewDialogHeader>
              <PreviewDialogTitle>
                {previewShortAnswer.title}
              </PreviewDialogTitle>
              <PreviewDialogHeaderActions>
                {previewShortAnswer?.id && !previewShortAnswer?.isSystem && (
                  <PreviewEditButton
                    type="button"
                    onClick={() => {
                      setShortAnswerPreviewDialog(false);
                      history.push(
                        `/tools/shortanswers/${previewShortAnswer.id}`
                      );
                    }}
                  >
                    <img src={editIcon} alt="Edit" width={16} height={16} />
                    <span>Edit</span>
                  </PreviewEditButton>
                )}
                <PopupTitleImg
                  onClick={() => setShortAnswerPreviewDialog(false)}
                  src={closecircle}
                />
              </PreviewDialogHeaderActions>
            </PreviewDialogHeader>
            <PreviewDialogContent>
              <CriteriaHeaderRow>
                <MarksHeaderLabel>Marks</MarksHeaderLabel>
                <CriteriaHeaderLabel>Criteria</CriteriaHeaderLabel>
              </CriteriaHeaderRow>
              <CriteriaListContainer>
                {previewShortAnswer.criterias
                  ?.slice()
                  .sort((a, b) => b.marks - a.marks)
                  .map((criteria, idx) => (
                    <CriteriaRow key={idx}>
                      <MarksBox>
                        {criteria.marksFrom != null
                          ? `${criteria.marksFrom}-${criteria.marks}`
                          : criteria.marks}
                      </MarksBox>
                      <CriteriaBox>{criteria.criteria}</CriteriaBox>
                    </CriteriaRow>
                  ))}
              </CriteriaListContainer>
            </PreviewDialogContent>
          </PreviewDialogContainer>
        </PopupBackground>
      )}
    </>
  );
}

export default ShortAnswers;
